/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Box from '@mui/material/Box';
import React from 'react';
import type { AdverseWeatherTAC } from '../../../types';

interface AdverseWeatherTACProps {
  TAC?: AdverseWeatherTAC;
  isActive: boolean;
}

interface TACWord {
  text?: string;
  type?: string;
}

// The backend should always return `tac` as an array of lines, where each line
// is an array of word objects. Guard against malformed/unexpected shapes so a
// single bad TAC response can never crash the whole workspace.
const toWords = (line: unknown): TACWord[] => {
  if (Array.isArray(line)) {
    return line as TACWord[];
  }
  if (typeof line === 'string') {
    return [{ text: line }];
  }
  if (line && typeof line === 'object') {
    return [line as TACWord];
  }
  return [];
};

export const AdverseWeatherTACMessage: React.FC<AdverseWeatherTACProps> = ({
  TAC,
  isActive,
}: AdverseWeatherTACProps) => {
  const lines: unknown[] = Array.isArray(TAC?.tac) ? TAC.tac : [];

  return (
    <Box
      data-testid="adverse-weather-tac"
      sx={{
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: '0.4px',
        color: 'geowebColors.captions.captionStatus.rgba',
      }}
    >
      {lines.map((line, index) => {
        const words = toWords(line);
        const lineKey = `${index}-${words.map((word) => word?.text ?? '').join(' ')}`;
        return (
          <Box
            component="span"
            key={lineKey}
            sx={{
              display: 'block',
            }}
          >
            {words.map((word, wordIndex) => {
              const text = word?.text ?? '';
              return (
                <Box
                  component="span"
                  key={text + wordIndex.toString()}
                  sx={{
                    display: 'inline',
                    boxDecorationBreak: 'clone',
                    WebkitBoxDecorationBreak: 'clone',
                    ...(isActive && {
                      backgroundColor:
                        'geowebColors.functional.warningHighlight.rgba',
                    }),
                    ...(word?.type === 'adverse' && {
                      color: 'geowebColors.captions.captionError.rgba',
                      fontWeight: 'bold',
                    }),
                  }}
                >
                  {text === '=' ? text : ` ${text}`}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};
